namespace e5 {
    export type SnapFunction = (point: IPoint, anchor?: SnapAnchor, element?: HTMLElement, parent?: HTMLElement) => IPoint;
    export interface SnapAnchor {
        x?: number | string;
        y?: number | string;
        uid?: number | string
    };

    export class SnapFunctions {
        public static points(points: IPoint[], radius: number = Number.MAX_VALUE): SnapFunction {
            return (point): IPoint => {
                var minDistance = radius;
                var result: IPoint = null;
                for (var p of points) {
                    var d = Point.distance(p, point);
                    if (d < minDistance) {
                        minDistance = d;
                        result = p;
                    }
                }
                return result;
            };
        }

        public static grid(gridX: number = 50, gridY: number = 50, radius: number = Number.MAX_VALUE): SnapFunction {
            var pt = new Point();
            return (point): IPoint => {
                pt.x = Calc.roundTo(point.x, gridX);
                pt.y = Calc.roundTo(point.y, gridY);
                return Point.distance(point, pt) < radius ? pt : null;
            }
        }

        public static corners(cornerWrap: number = 0, radius: number = 30): SnapFunction {
            var topLeft = new Point(cornerWrap, cornerWrap);
            var topRight = new Point(0, cornerWrap);
            var bottomRight = new Point();
            var bottomLeft = new Point(cornerWrap);

            var pts = this.points([topLeft, topRight, bottomRight, bottomLeft], radius);
            return (point, anchor, element, parent): IPoint => {
                topRight.x = parent.offsetWidth - cornerWrap;
                bottomRight.x = parent.offsetWidth - cornerWrap;
                bottomRight.y = parent.offsetHeight - cornerWrap;
                bottomLeft.y = parent.offsetHeight - cornerWrap;
                return pts(point);
            }
        }
    }

    export interface SnapOptions {
        /**
         * The snapping function.
         */
        snapFunction?: SnapFunction;
        /**
         * The local anchor/s.
         * Percentage values are relative the element offset-width/offset-height.
         */
        anchor?: SnapAnchor | SnapAnchor[];
        /**
         * The parent element where the snapping should be calculated.
         * If its set to auto the direct parent of the element will be used.
         */
        parent?: HTMLElement | "auto";
        /**
         * If set to true, then css transform is used.
         * If set to false, then css top/left is used.
         * 
         * @remarks
         * Its recommeded to prefer transform over top/left.
         */
        useTransform?: boolean;
        /**
         * Which transform mode should be used for coordinate transformation.
         */
        transformMode?: ITransformMode;
        /**
         * If this property is greater than 0, a tween is applied.
         * The value is in seconds.
         */
        tweenTime?: number;
        /**
         * Should the scrollTop and scrollLeft of the parent element be included.
         */
        includeScroll?: boolean;
        /**
         * The offset of the parent bounds.
         */
        offset?: IPoint;
    }

    export class Snap implements SnapOptions {
        public static DEFAULT: SnapOptions = {
            snapFunction: null,
            anchor: null,
            parent: "auto",
            useTransform: true,
            transformMode: TransformMode.Rectangle,
            tweenTime: 0,
            includeScroll: true,
            offset: null
        };

        private static _snap: Snap = new Snap();

        public snapFunction: SnapFunction;
        public anchor: SnapAnchor | SnapAnchor[];
        public parent: HTMLElement | "auto";
        public useTransform: boolean;
        public tweenTime: number;
        public includeScroll: boolean;
        public offset: IPoint;

        private _transform: Transform = new Transform();
        private _transformParent: Transform = new Transform();

        constructor(options: SnapOptions = null) {
            this.setOptions(options);
        }

        public setOptions(options: SnapOptions) {
            options = ObjectUtil.extend({}, Snap.DEFAULT, options);
            this.snapFunction = options.snapFunction;
            this.anchor = options.anchor;
            this.parent = options.parent;
            this.useTransform = options.useTransform;
            this.tweenTime = options.tweenTime;
            this.includeScroll = options.includeScroll;
            this.offset = options.offset;
        }

        public get transformMode(): ITransformMode {
            return this._transform.mode;
        }

        public set transformMode(mode: ITransformMode) {
            this._transform.mode = mode;
            this._transformParent.mode = mode;
        }

        protected apply(element: HTMLElement): SnapAnchor {
            if (!element) {
                console.warn("No element given to Snap.apply().");
                return null;
            }

            if (this.snapFunction === null) {
                console.warn("You need to set the 'snapFunction' property before calling Snap.apply().");
                return null;
            }

            var parentElement: HTMLElement = this.parent === "auto" ? element.parentElement : this.parent;

            this._transform.update(element);
            this._transformParent.update(parentElement);

            var anchors: SnapAnchor[] = Array.isArray(this.anchor) ? this.anchor : [this.anchor || { x: 0, y: 0 }];

            var snapX = NaN;
            var snapY = NaN;
            var minDistance = Number.MAX_VALUE;
            var finalAnchorX = 0;
            var finalAnchorY = 0;
            var localX = 0;
            var localY = 0;
            var anchorIndex = 0;
            var index = -1;

            for (var anchor of anchors) {
                index++;

                /**
                 * Resolve the anchor position. The values can be strings (top, left, "100%") or numbers.
                 */
                var anchorX = Calc.relationCalced(anchor.x, element.offsetWidth, 0);
                var anchorY = Calc.relationCalced(anchor.y, element.offsetHeight, 0);

                /**
                 * Snap in "parent" coordinate system.
                 */
                var localParent = this._transform.localToLocal(this._transformParent, anchorX, anchorY, 0);
                var clone = localParent.clone();

                if (this.includeScroll) {
                    clone.x += this._transformParent.element.scrollLeft;
                    clone.y += this._transformParent.element.scrollTop;
                }

                if (this.offset) {
                    clone.x -= this.offset.x;
                    clone.y -= this.offset.y;
                }

                var snappedPoint = this.snapFunction(clone, anchor, element, parentElement);
                if (!snappedPoint)
                    continue;

                if (this.offset) {
                    snappedPoint.x += this.offset.x;
                    snappedPoint.y += this.offset.y;
                }

                if (this.includeScroll) {
                    snappedPoint.x -= this._transformParent.element.scrollLeft;
                    snappedPoint.y -= this._transformParent.element.scrollTop;
                }

                /**
                 * Find minimun distance.
                 */
                var distance = Point.distance(snappedPoint, localParent);
                if (distance < minDistance) {
                    minDistance = distance;
                    finalAnchorX = anchorX;
                    finalAnchorY = anchorY;
                    snapX = snappedPoint.x;
                    snapY = snappedPoint.y;
                    localX = localParent.x;
                    localY = localParent.y;
                    anchorIndex = index;
                }
            }

            /**
             * Nothing to snap.
             */
            if (isNaN(snapX) || isNaN(snapY) || (localX === snapX && localY === snapY)) {
                return null;
            }

            /**
             * Transform back to element coordinate space.
             */
            var local = this._transformParent.localToLocal(this._transform, snapX, snapY, 0);

            /**
             * Apply the final position to the matrix.
             */
            local.x -= finalAnchorX;
            local.y -= finalAnchorY;
            this._transform.matrix.deltaTransform(local, local);
            this._transform.matrix.prependPosition(local);

            if (this.useTransform) {
                if (this.tweenTime > 0) {
                    gs.TweenMax.to(element, this.tweenTime, { matrix3d: this._transform.matrix, style: this._transform.style });
                }
                else {
                    element.style.transform = this._transform.matrix.getCss3d();
                }
            } else {
                var left = Calc.parse(element.style.left, 0) + this._transform.matrix.m41;
                var top = Calc.parse(element.style.top, 0) + this._transform.matrix.m42;
                if (this.tweenTime > 0) {
                    gs.TweenMax.to(element, this.tweenTime, { left: left, top: top });
                }
                else {
                    element.style.left = left + "px";
                    element.style.top = top + "px";
                }
            }

            return anchors[anchorIndex];
        }

        public dispose(): void {
            this.snapFunction = null;
            this.parent = null;
            this._transform.dispose();
            this._transform = null;
            this._transformParent.dispose();
            this._transformParent = null;
        }

        public static apply(element: HTMLElement, options: SnapOptions = null): SnapAnchor {
            this._snap.setOptions(options);
            return this._snap.apply(element);
        }
    }
}